import { uniq } from "ramda";
import { isTemporaryException } from "./http.js";
/**
 * Run a series of promises in sequence.
 * @param promises Promises.
 * @returns The results of the promises.
 */
export function promiseChain(promises) {
    return promises.reduce(async (promise, task) => {
        return promise.then(async (values) => {
            return values.concat([await task(values)]);
        });
    }, Promise.resolve(new Array()));
}
/**
 * Parse JSON and return a typed object.
 */
export function typedJson(json) {
    return JSON.parse(json);
}
/**
 * Throw an error.
 * @param error Error.
 * @returns A function that throws the error.
 */
export function throwError(error) {
    return () => {
        throw error;
    };
}
/**
 * Convert an error to a string.
 * @param e The error.
 * @returns A string.
 */
export function errorToString(e) {
    const causes = getCauses(e);
    return uniq(causes.map(cause => {
        if (typeof cause === "object" && cause !== null && "message" in cause && typeof cause.message === "string") {
            return cause.message;
        }
        return `${cause}`;
    })).join("\n");
}
/**
 * Get the root causes of an exception by traversing the cause property recursively.
 * @param e The exception.
 * @returns The root causes.
 */
export function getCauses(e) {
    if (e instanceof Error && e.cause !== undefined && e.cause !== null) {
        return [...getCauses(e.cause), e];
    }
    else {
        return [e];
    }
}
/**
 * Validate a filename. Returns an error string on failure, or nothing if successful.
 * @param fileName The file name.
 * @returns An optional error.
 */
export function validateFileName(fileName) {
    if (fileName.includes("/")) {
        return "Includes an invalid character: /";
    }
    return;
}
export function cleanFn(fn) {
    return fn.replace("/", "-");
}
export const INTERNET_TEST_TIMEOUT = 1000;
/**
 * Test to see if you are online. More reliable than navigator online.
 */
export async function testInternet(url) {
    try {
        const fetched = await fetch(url, { signal: AbortSignal.timeout(INTERNET_TEST_TIMEOUT) });
        return fetched.ok;
    }
    catch (e) {
        if (e instanceof DOMException) {
            if (e.name === "AbortError") {
                return false;
            }
        }
        if (isTemporaryException(e)) {
            return false;
        }
        throw e;
    }
}
export function getMode(modeString) {
    if (modeString.toLowerCase() === "development" || modeString.toLowerCase() === "dev") {
        return "dev";
    }
    else if (modeString.toLowerCase() === "production" || modeString.toLowerCase() === "prod") {
        return "prod";
    }
    else {
        throw new Error("Invalid mode: " + modeString + ".");
    }
}
/**
 * Shorten a string and add an ellipsis if it is too long.
 * @param string The string.
 * @param length The maximum length.
 * @returns A shortened string.
 */
export function shorten(string, length = 15) {
    if (string.length > length) {
        return string.slice(0, length) + "...";
    }
    return string;
}
export const ASSET_DELIMITER = " / ";
